import { VideoFragment } from '@lib/umbraco/generated/types.generated'
import clsx from 'clsx'
import Head from 'next/head'
import { CSSProperties, FC, useEffect, useMemo, useState } from 'react'
import useDimensions from 'react-cool-dimensions'
import { useInView } from 'react-intersection-observer'
import { ISublimeVideoLoader, SublimeVideoLoader } from '../Loaders/VideoLoader'
import s from './VideoRender.module.scss'
type VideoRenderProps = {
  video: VideoFragment
  className?: string
  priority?: boolean
  caption?: string
  hideCaption?: boolean
  aspectRatio?: CSSProperties['aspectRatio']
}

const VideoRender: FC<VideoRenderProps> = ({
  video,
  className,
  priority,
  caption,
  hideCaption,
  aspectRatio,
}) => {
  const { observe, width, height } = useDimensions<HTMLDivElement | null>()
  const sublimeVideoLoader: ISublimeVideoLoader =
    useMemo<ISublimeVideoLoader>(() => {
      return new SublimeVideoLoader(video, width)
    }, [video, width])
  const [posterUrl, setPosterUrl] = useState<string | undefined>(undefined)
  const { ref, inView, entry } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '0px 0px 250px 0px',
  })

  useEffect(() => {
    if (inView) {
      for (var source in entry.target.children) {
        var videoSource = entry.target.children[source]
        if (
          typeof videoSource.tagName === 'string' &&
          videoSource.tagName === 'SOURCE'
        ) {
          // @ts-ignore
          videoSource.src = videoSource.dataset.src
        }
        // @ts-ignore
        entry.target.load()
      }
    }
  }, [entry])

  useEffect(() => {
    if (sublimeVideoLoader.posterLoader()) {
      fetch(sublimeVideoLoader.posterLoader())
        .then((response) => {
          if (response.ok) {
            setPosterUrl(sublimeVideoLoader.posterLoader())
          } else {
            setPosterUrl(undefined)
          }
        })
        .catch((e) => {})
    }
  }, [sublimeVideoLoader.posterLoader()])
  if (!video?.url) {
    return <></>
  }

  return (
    <div
      className="relative flex flex-col h-full justify-center overflow-hidden"
      style={{ aspectRatio }}
      ref={observe}
    >
      <video
        ref={ref}
        className={clsx(s.video, className)}
        autoPlay={!video.disableAutoplay}
        controls={video.enableControls}
        muted={!video.disableAutoplay && !video.enableSound}
        loop={!video.disableLoop}
        playsInline
        poster={posterUrl}
        preload="auto"
      >
        <source
          data-src={`${sublimeVideoLoader.videoLoader()}`}
          // src={`${sublimeVideoLoader.videoLoader()}`}
          type="video/mp4"
        />
        Your browser does not support HTML video.
      </video>
      {!hideCaption && <div className="caption">{caption}</div>}
      {priority && posterUrl ? (
        <Head>
          <link
            key={'Poster Image'}
            rel="preload"
            as="image"
            href={posterUrl}
          />
        </Head>
      ) : null}
    </div>
  )
}

export default VideoRender
